import { pmt } from '@/pages/blueprint-lite/utils/finance';
import {
  formatAud,
  formatPercent,
} from '@/pages/blueprint-lite/utils/formatters';

import type {
  LenderAssessment,
  LenderProse,
  PurchaseAssessmentResult,
} from '../../../assessment/types';
import s from '../styles/dashboard.module.css';

// Loan term for the display-only monthly-repayment figure. The engine
// reads the canonical value from the live calculation-settings API
// (`settings.loanTermMonths`); 30 years (360 months) is the AU standard
// and matches the seed value the API currently returns.
const DISPLAY_LOAN_TERM_MONTHS = 360;

const VERDICT_LABEL: Record<LenderAssessment['verdict'], string> = {
  pass: 'Eligible',
  amber: 'Borderline',
  fail: 'Ineligible',
};

function LenderCard({
  lender,
  prose,
  loanAmount,
  rank,
  isRecommended,
}: {
  lender: LenderAssessment;
  prose: LenderProse | undefined;
  loanAmount: number;
  rank: number;
  isRecommended: boolean;
}) {
  const rate =
    lender.assessmentRate - lender.assessmentRateBuffer || lender.assessmentRate;
  const cappedLoan =
    lender.maxLoan > 0 ? Math.min(loanAmount, lender.maxLoan) : loanAmount;
  const monthly =
    cappedLoan > 0 ? pmt(cappedLoan, rate, DISPLAY_LOAN_TERM_MONTHS) : 0;

  return (
    <article
      className={`${s.lenderCard} ${isRecommended ? s.recommended : ''}`}
    >
      <div className={s.lenderHd}>
        <span className={s.lenderRank}>Rank {String(rank).padStart(2, '0')}</span>
        {isRecommended && <span className={s.lenderRecPill}>Recommended</span>}
      </div>
      <h3 className={s.lenderName}>{lender.displayName}</h3>
      <span className={`${s.lenderVerdict} ${s[lender.verdict]}`}>
        {VERDICT_LABEL[lender.verdict]}
      </span>

      <div className={s.lenderStats}>
        <div>
          <div className={s.lenderStatLabel}>Borrowing power</div>
          <div className={s.lenderStatValue}>{formatAud(lender.maxLoan)}</div>
          <div className={s.lenderStatSub}>at this lender</div>
        </div>
        <div>
          <div className={s.lenderStatLabel}>Interest rate</div>
          <div className={s.lenderStatValue}>{formatPercent(rate, 2)}</div>
          <div className={s.lenderStatSub}>
            p.a. · {formatAud(monthly)}/mo
          </div>
        </div>
      </div>

      {prose && (
        <div className={s.lenderProsConsBlock}>
          <div className={`${s.lenderListLabel} ${s.lenderProsLabel}`}>
            Why it works
          </div>
          <ul className={`${s.lenderList} ${s.pros}`}>
            {prose.pros.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
          <div className={`${s.lenderListLabel} ${s.lenderConsLabel}`}>
            What to watch
          </div>
          <ul className={`${s.lenderList} ${s.cons}`}>
            {prose.cons.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

interface Props {
  result: PurchaseAssessmentResult;
}

const LendersPanel = ({ result }: Props) => {
  const top3 = result.lenders.slice(0, 3);
  const rest = result.lenders.slice(3);
  const proseByName = Object.fromEntries(
    result.lenderProse.map((p) => [p.lenderName, p]),
  );

  return (
    <>
      <div className={s.lenderGrid}>
        {top3.map((l, i) => (
          <LenderCard
            key={l.name}
            lender={l}
            prose={proseByName[l.name]}
            loanAmount={result.loan.loanAmount}
            rank={i + 1}
            isRecommended={i === 0 && l.verdict !== 'fail'}
          />
        ))}
      </div>

      {rest.length > 0 && (
        <div className={s.lenderStrip}>
          {rest.map((l) => (
            <div key={l.name} className={s.lenderStripCell}>
              <div className={s.lenderStripHead}>
                <span className={s.lenderStripName}>{l.displayName}</span>
                <span className={`${s.lenderStripVerdict} ${s[l.verdict]}`}>
                  {VERDICT_LABEL[l.verdict]}
                </span>
              </div>
              <div className={s.lenderStripValue}>
                Max {formatAud(l.maxLoan)} · {l.dtiDisplay}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default LendersPanel;
